import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { Button } from '../ui';

interface NavItem {
  to: string;
  label: string;
  icon: string;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const SECTIONS: NavSection[] = [
  {
    title: 'Content',
    items: [
      { to: '/', label: 'Dashboard', icon: '◈' },
      { to: '/render-farm', label: 'Render Farm', icon: '⚙' },
      { to: '/brands', label: 'Brands', icon: '◆' },
      { to: '/fbo', label: 'FBO', icon: '₿' },
      { to: '/wbo', label: 'Wiedza Bez Owijania', icon: '◉' },
      { to: '/fbo/finance-studio', label: 'FBO Finance Studio', icon: '₿' },
      { to: '/bloodcrimes', label: 'BloodCrimes', icon: '🩸' },
      { to: '/bloodcrimes/city-reels', label: 'BC City Reels', icon: '🎬' },
      { to: '/bloodcrimes/youtube', label: 'BC YouTube Case Files', icon: '▰' },
      { to: '/bloodcrimes/tv', label: 'BloodCrimesTV Studio', icon: '📺' },
      { to: '/comedy', label: 'Comedy Studio', icon: '🎭' },
      { to: '/nieboguard', label: 'NieboGuard Media Studio', icon: '🛰' },
      { to: '/distribution', label: 'Distribution Hub', icon: '⇄' },
      { to: '/riftcells/gameplay', label: 'RiftCells Gameplay', icon: '⚡' },
      { to: '/riftcells/longform', label: 'RiftCells YouTube Long-form', icon: '▰' },
      { to: '/riftcells/educational', label: 'RiftCells Educational', icon: '◉' },
      { to: '/remotion-lab', label: 'Remotion Lab', icon: '◉' },
      { to: '/content-studio', label: 'Content Studio', icon: '✦' },
      { to: '/ideas', label: 'Ideas', icon: '✎' },
      { to: '/campaigns', label: 'Campaigns', icon: '⚑' },
      { to: '/calendar', label: 'Calendar', icon: '▦' },
      { to: '/review', label: 'Review Queue', icon: '✓' },
      { to: '/publishing', label: 'Publishing', icon: '➤' },
      { to: '/media', label: 'Media Library', icon: '▣' },
      { to: '/image-studio', label: 'Image Studio', icon: '▤' },
      { to: '/ai-routing', label: 'AI Routing', icon: '⇄' },
      { to: '/ai-costs', label: 'AI Costs', icon: '€' },
      { to: '/video-jobs', label: 'Video Jobs', icon: '▶' },
      { to: '/pins', label: 'PINS', icon: '⇅' },
      { to: '/pins/long-video', label: 'PINS Long Video', icon: '▰' },
      { to: '/pins-content', label: 'PINS Content', icon: '◈' },
      { to: '/video-templates', label: 'Video Templates', icon: '▣' },
    ],
  },
  {
    title: 'Knowledge',
    items: [
      { to: '/sources', label: 'Sources', icon: '⇲' },
      { to: '/brand-brain', label: 'Brand Brain', icon: '◉' },
    ],
  },
  {
    title: 'Configuration',
    items: [
      { to: '/social-accounts', label: 'Social Accounts', icon: '☍' },
      { to: '/ai-providers', label: 'AI Providers', icon: '⚙' },
      { to: '/prompts', label: 'Prompt Library', icon: '⌘' },
    ],
  },
  {
    title: 'Insights',
    items: [
      { to: '/analytics', label: 'Analytics', icon: '▤' },
      { to: '/costs', label: 'Costs', icon: '₿' },
      { to: '/settings', label: 'Settings', icon: '⚙' },
    ],
  },
];

const TITLES: Record<string, string> = {
  '/': 'Dashboard',
  '/brands': 'Brands',
  '/fbo': 'FBO Content Engine',
  '/wbo': 'Wiedza Bez Owijania Studio',
  '/fbo/finance-studio': 'FBO Finance Studio',
  '/bloodcrimes': 'BloodCrimes Content Engine',
  '/bloodcrimes/youtube': 'BloodCrimes Case Files',
  '/bloodcrimes/tv': 'BloodCrimesTV Studio',
  '/comedy': 'AI Comedy Mashup Studio',
  '/riftcells/gameplay': 'RiftCells Gameplay Content Engine',
  '/riftcells/longform': 'RiftCells YouTube Long-form',
  '/riftcells/educational': 'RiftCells Educational Studio',
  '/fbo/remotion-lab': 'Remotion Lab',
  '/remotion-lab': 'Remotion Lab',
  '/content-studio': 'Content Studio',
  '/image-studio': 'Image Studio',
  '/ai-routing': 'AI Routing',
  '/ai-costs': 'AI Costs',
  '/video-jobs': 'Video Jobs',
  '/pins': 'PINS',
  '/pins/long-video': 'PINS Long Video Studio',
  '/pins-content': 'PINS Content',
  '/render-farm': 'Render Farm',
  '/video-templates': 'Video Templates',
  '/ideas': 'Ideas',
  '/campaigns': 'Campaigns',
  '/calendar': 'Calendar',
  '/review': 'Review Queue',
  '/publishing': 'Publishing',
  '/media': 'Media Library',
  '/sources': 'Sources',
  '/brand-brain': 'Brand Brain',
  '/social-accounts': 'Social Accounts',
  '/ai-providers': 'AI Providers',
  '/prompts': 'Prompt Library',
  '/analytics': 'Analytics',
  '/costs': 'Costs',
  '/settings': 'Settings',
};

export function AppLayout() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const title = TITLES[location.pathname] ?? (location.pathname.startsWith('/content/') ? 'Content' : 'Kontentowo');

  return (
    <div className="app">
      <aside className={`sidebar${open ? ' sidebar--open' : ''}`}>
        <div className="sidebar__brand">
          <span className="sidebar__logo">K</span>
          Kontentowo
        </div>
        {SECTIONS.map((section) => (
          <div key={section.title}>
            <div className="sidebar__section">{section.title}</div>
            {section.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) => `sidebar__link${isActive ? ' sidebar__link--active' : ''}`}
                onClick={() => setOpen(false)}
              >
                <span className="sidebar__icon">{item.icon}</span>
                {item.label}
              </NavLink>
            ))}
          </div>
        ))}
        <div className="sidebar__footer">Kontentowo · Content OS</div>
      </aside>

      <div className="main">
        <header className="topbar">
          <button className="menu-toggle" onClick={() => setOpen((v) => !v)} aria-label="Menu">
            ☰
          </button>
          <span className="topbar__title">{title}</span>
          <div className="topbar__spacer" />
          {user && (
            <div className="inline" style={{ gap: 8 }}>
              <span className="muted" style={{ fontSize: 12 }}>
                {user.email}
              </span>
              <Button size="sm" onClick={() => logout()}>
                Wyloguj
              </Button>
            </div>
          )}
        </header>
        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
